// TESSERAX — méta persistante inter-parties (déblocages de reliques + stats).
// Stockée en localStorage ; dégradation gracieuse (mode privé / quota).
// Pur côté logique de fusion (mergeUnlocks testable) ; l'I/O est isolée.
import { STARTER_UNLOCKS, unlocksFor } from './relics';
import { defaultOwned } from './shop';
import * as ach from './achievements';

const LS_META = 'tesserax:meta';
// Version du schéma de méta persistée. Toute lecture (loadMeta) NORMALISE vers la forme
// courante (cf. sanitizeMeta) — la migration est donc tolérante par défaut (champs
// manquants/typés faux → valeurs sûres). On versionne quand même pour pouvoir, à l'avenir,
// brancher des migrations CIBLÉES (renommage de champ, recalcul) sans deviner l'origine.
const META_VERSION = 1;

// Entier sûr ≥ min (défaut 0) : non fini / négatif hors-borne → fallback. Tronque les décimaux.
function safeInt(v: unknown, fallback = 0, min = 0): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n >= min ? Math.floor(n) : fallback;
}
// Tableau de chaînes propre : filtre tout ce qui n'est pas une string (tolère un blob corrompu).
function strArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}
// Décompte cumulé { clé: entier ≥0 } sûr (grades, etc.) : ignore les entrées non valides.
function countMap(v: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    for (const [k, n] of Object.entries(v as Record<string, unknown>)) {
      const c = safeInt(n, 0, 0); if (c > 0) out[k] = c;
    }
  }
  return out;
}

// `croix`/`tesseras`/`continuum`/`zoneLines` : cumuls de PRATIQUE par archétype (Voies, cf.
// core/voies) — Croix réussies, TESSERAX, clears de continuum (b2b≥2), lignes-équivalent
// banquées aux sorties de Zone. Absents des vieilles sauvegardes → sanitizeMeta les pose à 0.
export interface MetaStats {
  runs: number; bascules: number; perfects: number; bestScore: number;
  croix: number; tesseras: number; continuum: number; zoneLines: number;
}
// `currency` se DÉPENSE désormais en cosmétiques (cf. core/shop) ; `owned`/`equipped`
// portent ces achats ; `streak`/`lastDaily` la série de défis quotidiens (rétention).
export interface Meta {
  unlocked: string[]; stats: MetaStats; currency: number; achievements: string[];
  owned: string[]; equipped: { chamber?: string; title?: string; seal?: string; card?: string }; streak: number; lastDaily: string;
  // Paliers de l'échelle de défis validés (cf. core/challenges) — optionnel pour la
  // rétro-compat (méta sérialisée avant l'ajout, et littéraux de test partiels).
  completedChallenges?: string[];
  // MOT-STRATA-PERSIST-001 — plus haute strate ATTEINTE en mode Strates (index 0-based,
  // -1 = jamais joué). L'ascension repart de zéro à chaque run (intention roguelite
  // « fraîche »), mais ce record PERSISTE → trace de progression + relance (« bats ton
  // ascension »). Purement informatif/rétention : aucun effet sur le gameplay d'une run.
  bestAscent?: number;
  // MOT-MASTERY-TRACK-001 — décompte CUMULÉ des grades de fin (S/A/B/C/D) obtenus, façon
  // « tu as atteint S 7 fois ». Pur jalon de maîtrise long terme, COSMÉTIQUE : n'ouvre
  // aucun avantage de jeu (classement équitable, équilibrage intact).
  grades?: Record<string, number>;
  // RET-STRATES-NGPLUS-001 — nombre d'ASCENSIONS Strates COMPLÉTÉES (7 strates franchies).
  // Donne une suite/relance après la seule « vraie victoire » longue : « tu as bouclé
  // l'ascension N fois ». Purement informatif/rétention : aucun rééquilibrage de difficulté
  // (l'ascension reste identique — un vrai NG+ escaladé est laissé en attente de validation).
  ascensions?: number;
  // Version du schéma persisté (cf. META_VERSION) — écrite par saveMeta, lue par loadMeta.
  version?: number;
}

// Incrémente le décompte cumulé d'un grade de fin de partie. PUR/immuable.
export function recordGrade(m: Meta, grade: string): Meta {
  if (!grade) return m;
  const g = { ...(m.grades || {}) };
  g[grade] = (g[grade] || 0) + 1;
  return { ...m, grades: g };
}

// Enregistre l'ascension atteinte (index 0-based de la plus haute strate validée).
// PUR : renvoie la méta à jour + `isRecord` (nouveau sommet) pour la célébration UI.
// Ne régresse jamais le record (max). main.ts persiste via saveMeta.
export function recordAscent(m: Meta, ascentReached: number): { meta: Meta; isRecord: boolean } {
  const prev = (m.bestAscent ?? -1);
  if (!(ascentReached > prev)) return { meta: m, isRecord: false };
  return { meta: { ...m, bestAscent: ascentReached }, isRecord: true };
}

// Incrémente le décompte d'ascensions Strates complétées (7 strates franchies). PUR/immuable.
export function recordAscensionDone(m: Meta): Meta {
  return { ...m, ascensions: ((m.ascensions ?? 0) + 1) };
}

function fresh(): Meta {
  return { unlocked: STARTER_UNLOCKS.slice(), stats: { runs: 0, bascules: 0, perfects: 0, bestScore: 0, croix: 0, tesseras: 0, continuum: 0, zoneLines: 0 }, currency: 0, achievements: [],
    owned: defaultOwned(), equipped: {}, streak: 0, lastDaily: '', completedChallenges: [], bestAscent: -1, grades: {}, ascensions: 0, version: META_VERSION };
}

// `lastDaily` sain : chaîne 'YYYYMMDD' (8 chiffres) ou '' (jamais joué). Tout autre format
// (corrompu, futur lointain, type faux) → '' : streakStatus/bumpStreak repartent proprement.
function safeDay(v: unknown): string {
  return typeof v === 'string' && /^\d{8}$/.test(v) ? v : '';
}

// `equipped` sain : seules les clés cosmétiques attendues, valeurs string. Un blob corrompu
// (ex. { chamber: 123 }) est nettoyé sans casser le rendu.
function safeEquipped(v: unknown): Meta['equipped'] {
  const out: Meta['equipped'] = {};
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    for (const k of ['chamber', 'title', 'seal', 'card'] as const) if (typeof o[k] === 'string') out[k] = o[k] as string;
  }
  return out;
}

// NORMALISE un blob (potentiellement corrompu/partiel/ancien) vers une Meta complète et sûre.
// Aucune entrée invalide ne peut produire NaN / type faux / état impossible (cf. ROB-SAVE-002).
// Pur/testable : aucune I/O. loadMeta l'enveloppe d'un try/catch + JSON.parse.
export function sanitizeMeta(raw: unknown): Meta {
  const m = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw as Partial<Meta> : {};
  const ms = (m.stats && typeof m.stats === 'object' && !Array.isArray(m.stats)) ? m.stats as Partial<MetaStats> : {};
  const stats: MetaStats = {
    runs: safeInt(ms.runs), bascules: safeInt(ms.bascules), perfects: safeInt(ms.perfects), bestScore: safeInt(ms.bestScore),
    croix: safeInt(ms.croix), tesseras: safeInt(ms.tesseras), continuum: safeInt(ms.continuum), zoneLines: safeInt(ms.zoneLines),
  };
  const owned = Array.from(new Set([...defaultOwned(), ...strArray(m.owned)]));   // rétro-compat : socle garanti
  return {
    unlocked: mergeUnlocks(strArray(m.unlocked), stats), stats,
    currency: safeInt(m.currency), achievements: strArray(m.achievements),
    owned, equipped: safeEquipped(m.equipped), streak: safeInt(m.streak), lastDaily: safeDay(m.lastDaily),
    completedChallenges: strArray(m.completedChallenges),
    bestAscent: Number.isFinite(m.bestAscent as number) ? Math.floor(m.bestAscent as number) : -1,
    grades: countMap(m.grades), ascensions: safeInt(m.ascensions), version: META_VERSION,
  };
}

// Marque un défi comme validé (idempotent). Pur : renvoie une méta à jour, ou la même
// instance si le défi était déjà acquis. main.ts persiste via saveMeta.
export function withChallengeDone(m: Meta, id: string): Meta {
  const done = m.completedChallenges || [];
  if (done.includes(id)) return m;
  return { ...m, completedChallenges: [...done, id] };
}

// Différence en JOURS entre deux graines 'YYYYMMDD' (UTC). NaN si format invalide.
function dayDiff(a: string, b: string): number {
  const p = (s: string) => Date.UTC(+s.slice(0, 4), +s.slice(4, 6) - 1, +s.slice(6, 8));
  return Math.round((p(b) - p(a)) / 86400000);
}

// Met à jour la série de défis quotidiens à l'arrivée du jour `day` (todaySeed).
// Pur/testable : jour suivant → +1 ; même jour → inchangé.
// MOT-STREAK-001 — PARDON D'UN JOUR : un seul jour manqué (écart de 2 jours) ne CASSE
// plus la série, elle continue de monter (1 grâce). Avant, un unique jour raté
// réinitialisait tout à 1 → punition sèche démotivante. Un trou de ≥2 jours réinitialise
// toujours. (La série n'influence que la rétention/monnaie cosmétique, jamais le
// gameplay ni la RNG → aucun impact déterminisme/équilibrage.)
// `bonus` = monnaie cosmétique offerte quand la série PROGRESSE (0 sinon).
export function bumpStreak(streak: number, lastDaily: string, day: string): { streak: number; lastDaily: string; bonus: number } {
  if (!day) return { streak, lastDaily, bonus: 0 };
  if (lastDaily === day) return { streak: Math.max(1, streak), lastDaily, bonus: 0 };
  const d = lastDaily ? dayDiff(lastDaily, day) : Infinity;
  const ns = (d === 1 || d === 2) ? streak + 1 : 1;   // d===2 : un jour manqué, pardonné
  return { streak: ns, lastDaily: day, bonus: 200 + (ns - 1) * 100 };
}

// État de la SÉRIE de défis quotidiens pour l'AFFICHAGE (boucle de rétention
// « one more day », façon Duolingo). PUR/testable : ne lit jamais l'horloge —
// main.ts passe `today` (todaySeed du jour). main.ts n'a plus qu'à habiller
// l'état d'un libellé i18n et d'un style.
//   · 'none'    : aucune série en cours → rien à montrer.
//   · 'secured' : le défi du jour est DÉJÀ relevé → la série est acquise pour aujourd'hui.
//   · 'risk'    : la série est VIVANTE mais le défi du jour n'est pas encore relevé
//                 (dernier jour joué = hier) → « joue aujourd'hui pour la garder » :
//                 c'est l'état qui doit accrocher l'œil et faire revenir.
//   · 'broken'  : un jour a été manqué → la série retombera à 1 au prochain défi.
export type StreakState = 'none' | 'secured' | 'risk' | 'broken';
export function streakStatus(streak: number, lastDaily: string, today: string): { count: number; state: StreakState } {
  if (streak <= 0 || !lastDaily || !today) return { count: streak > 0 ? streak : 0, state: 'none' };
  if (lastDaily === today) return { count: streak, state: 'secured' };
  const d = dayDiff(lastDaily, today);
  // Pardon d'un jour (cf. bumpStreak) : un écart de 1 OU 2 jours reste RATTRAPABLE
  // en jouant aujourd'hui → 'risk' (l'œil doit accrocher). ≥3 jours → 'broken'.
  if (d === 1 || d === 2) return { count: streak, state: 'risk' };
  if (Number.isFinite(d) && d > 2) return { count: streak, state: 'broken' };
  // d ≤ 0 ou NaN (lastDaily dans le futur / format invalide) → on ne présume rien : acquis.
  return { count: streak, state: 'secured' };
}

// Fusionne les déblocages : starters + ceux mérités par les stats. Pur/testable.
export function mergeUnlocks(current: string[], stats: MetaStats): string[] {
  const set = new Set([...STARTER_UNLOCKS, ...current, ...unlocksFor(stats)]);
  return Array.from(set);
}

export function loadMeta(): Meta {
  try {
    const raw = localStorage.getItem(LS_META);
    if (!raw) return fresh();
    return sanitizeMeta(JSON.parse(raw));   // normalisation tolérante (corrompu/partiel/ancien → sûr)
  } catch { return fresh(); }
}

export function saveMeta(m: Meta): void {
  try { localStorage.setItem(LS_META, JSON.stringify(m)); } catch { /* ignore */ }
}

// Temps forts d'une partie passés à recordRun (au-delà des cumuls).
export interface RunHighlights {
  bascules: number; perfects: number; score: number;
  level?: number; chain?: number; slabs?: number; lines?: number; combo?: number; cross?: number; b2b?: number; daily?: boolean; zen?: boolean;
  // Comptes de PRATIQUE de la run (Voies) : cumulés dans MetaStats par recordRun.
  // `cross` ci-dessus sert déjà aux succès ET au cumul `croix` ; les trois autres arrivent ici.
  tesseras?: number; continuum?: number; zoneLines?: number;
  day?: string;   // graine 'YYYYMMDD' du jour si défi du jour réel (pour la série)
}

// Enregistre la fin d'une partie : met à jour stats, recalcule les déblocages de
// reliques ET les succès, renvoie la méta à jour + ce qui est NOUVELLEMENT obtenu.
export function recordRun(m: Meta, run: RunHighlights): { meta: Meta; newly: string[]; newlyAch: string[]; streakUp: boolean; streakBonus: number } {
  const before = new Set(m.unlocked);
  // Un compte de run corrompu (NaN/négatif) ne doit jamais empoisonner un cumul (monotone).
  const inc = (n?: number) => (Number.isFinite(n) && (n as number) > 0 ? Math.floor(n as number) : 0);
  const stats: MetaStats = {
    runs: m.stats.runs + 1,
    bascules: m.stats.bascules + run.bascules,
    perfects: m.stats.perfects + run.perfects,
    bestScore: Math.max(m.stats.bestScore, run.score),
    croix: (m.stats.croix || 0) + inc(run.cross),
    tesseras: (m.stats.tesseras || 0) + inc(run.tesseras),
    continuum: (m.stats.continuum || 0) + inc(run.continuum),
    zoneLines: (m.stats.zoneLines || 0) + inc(run.zoneLines),
  };
  const unlocked = mergeUnlocks(m.unlocked, stats);
  const newly = unlocked.filter(id => !before.has(id));
  // Succès : cumuls + temps forts de la partie qui vient de se terminer.
  const beforeA = new Set(m.achievements || []);
  const achievements = ach.evaluate(m.achievements || [], {
    runs: stats.runs, bascules: stats.bascules, perfects: stats.perfects, bestScore: stats.bestScore,
    score: run.score, level: run.level, chain: run.chain, slabs: run.slabs, lines: run.lines, combo: run.combo, cross: run.cross, b2b: run.b2b, daily: run.daily, zen: run.zen,
  });
  const newlyAch = achievements.filter(id => !beforeA.has(id));
  // Monnaie : gain de partie + bonus de série de défis quotidiens (rétention). Garde NaN :
  // un score corrompu (NaN/Infinity/négatif) n'empoisonne jamais la monnaie (sinon tout
  // achat futur deviendrait impossible — NaN ≥ prix est toujours faux). Borné au safe-integer.
  const base = Number.isFinite(m.currency) ? m.currency : 0;
  const scoreReward = Number.isFinite(run.score) && run.score > 0 ? Math.floor(run.score / 1000) : 0;
  let currency = base + scoreReward;
  let streak = m.streak, lastDaily = m.lastDaily, streakBonus = 0;
  if (run.daily && run.day) { const s = bumpStreak(m.streak, m.lastDaily, run.day); streak = s.streak; lastDaily = s.lastDaily; currency += s.bonus; streakBonus = s.bonus; }
  currency = Math.min(Number.MAX_SAFE_INTEGER, Math.max(0, currency));
  const meta: Meta = { unlocked, stats, currency, achievements, owned: m.owned, equipped: m.equipped, streak, lastDaily, completedChallenges: m.completedChallenges || [], bestAscent: m.bestAscent ?? -1, grades: m.grades || {}, ascensions: m.ascensions ?? 0, version: META_VERSION };
  // `streakUp` = la série de défis a PROGRESSÉ ce run (nouveau jour) → de quoi célébrer
  // côté UI ; le bonus de résonance est déjà intégré à `currency` (on l'expose pour l'afficher).
  return { meta, newly, newlyAch, streakUp: streakBonus > 0, streakBonus };
}
